// @ts-strict-ignore
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@tolgee/ngx';
import { BehaviorSubject, Subject } from 'rxjs';
import { DEFAULT_LANGUAGE } from '../app.constants';
import { UI_STRINGS } from '../functions/language/uistrings';
import { ClientFeatureFlagName } from '../model/client-model/client-feature-flag';
import { Language, simpleLanguageCodes } from '../model/enums/language';
import { LanguageOption } from '../model/enums/language-option';
import { BackendFeatureFlagsService } from './backend/backend-feature-flags.service';
import { AppCookieService } from './cookie-service/app-cookie.service';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  onLanguageChange: Subject<void> = new Subject();
  boardLanguage: BehaviorSubject<Language> = new BehaviorSubject<Language>(null);
  translationsLoaded$ = new BehaviorSubject<Language[]>([]);
  userLanguage: BehaviorSubject<Language> = new BehaviorSubject<Language>(undefined);

  get language(): Language {
    if (!this._language) {
      this._getUserLanguage();
    }

    return this._language;
  }

  set language(language: Language) {
    if (this._language !== language) {
      this._language = language;
      this._tolgee.changeLanguage(language).then(() => {
        this.translationsLoaded$.next([...new Set([...this.translationsLoaded$.value, language])]);
        this._document.documentElement.lang = this.getDisplayLanguageCode(language);
        this._cookies.setPreference('userLanguage', language);
        this.onLanguageChange.next();
        this.userLanguage.next(language);
      });
    }
  }

  get twoLetterLanguageCode(): Language {
    return this.getTwoLetterLanguageCode(this.language);
  }

  /** @returns localized dateformat for primeng components e.g. yy-mm-dd for swedish and mm/dd/yy for american english */
  get dateFormat(): string {
    const format = new Intl.DateTimeFormat(this.getFourLetterLanguageCode(this.language))
      .formatToParts()
      .map((e) => {
        switch (e.type) {
          case 'month':
            return 'mm';
          case 'day':
            return 'dd';
          case 'year':
            return 'yy';
          default:
            return e.value;
        }
      })
      .join('');
    return format;
  }
  /** @returns localized dateformat for primeng components e.g. mm-yy for swedish and mm/yy for american english */
  get dateFormatMonthYear(): string {
    const format = new Intl.DateTimeFormat(this.getFourLetterLanguageCode(this.language)).formatToParts();
    const delimeter = format.find((p) => p.type === 'literal').value;

    return format
      .map((e) => {
        switch (e.type) {
          case 'month':
            return 'mm';
          case 'day':
            return;
          case 'year':
            return 'yy';
          default:
            return;
        }
      })
      .filter((p) => p)
      .join(delimeter);
  }

  /** @returns localized dateformat with the full year suitable for labels, placeholders etc., e.g. yyyy-mm-dd for swedish and mm/dd/yyyy for american english */
  get dateFormatFullYear(): string {
    return this.dateFormat.replace('yy', 'yyyy');
  }

  private _language: Language;
  private _supportedLanguages: Language[];
  private _supportedLanguageVariants: Language[];

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _cookies: AppCookieService,
    private _featureFlagsService: BackendFeatureFlagsService,
    private _tolgee: TranslateService
  ) {
    this._supportedLanguages = Object.keys(Language)
      .filter((key) => isNaN(Number(Language[key])))
      .filter((key) => simpleLanguageCodes.includes(Language[key]))
      .map((key) => Language[key]);
    this._supportedLanguageVariants = Object.keys(Language)
      .filter((key) => isNaN(Number(Language[key])))
      .filter((key) => !simpleLanguageCodes.includes(Language[key]))
      .map((key) => Language[key]);

    if (!this.userLanguage.value) {
      this.userLanguage.next(this.language);
    }
  }

  getAllLanguagesList(): LanguageOption[] {
    return Object.keys(Language)
      .filter((language) => {
        if (Language[language] === Language.SWEDISH_PA) {
          return this._featureFlagsService.isEnabled(ClientFeatureFlagName.Panorama);
        }
        return true;
      })
      .map(this._convertToLanguageOption);
  }

  getDisplayLanguageCode(language: Language): Language {
    switch (language) {
      case Language.ENGLISH_PA:
        return Language.ENGLISH_GB;
      case Language.SWEDISH_PA:
        return Language.SWEDISH_SE;
      default:
        return language;
    }
  }

  getFourLetterLanguageCode(language: Language): Language {
    switch (language) {
      case Language.ENGLISH:
      case Language.ENGLISH_PA:
        return Language.ENGLISH_GB;
      case Language.SWEDISH:
      case Language.SWEDISH_PA:
        return Language.SWEDISH_SE;
      case Language.GERMAN:
        return Language.GERMAN_DE;
      case Language.FRENCH:
        return Language.FRENCH_FR;
      case Language.SPANISH:
        return Language.SPANISH_ES;
      case Language.DUTCH:
        return Language.DUTCH_NL;
      default:
        return language;
    }
  }

  getLanguageSelectionList(): LanguageOption[] {
    return Object.keys(Language)
      .filter((language) => !simpleLanguageCodes.includes(Language[language]))
      .filter((language) => {
        if (Language[language] === Language.SWEDISH_PA || Language[language] === Language.ENGLISH_PA) {
          return this._featureFlagsService.isEnabled(ClientFeatureFlagName.Panorama);
        }
        return true;
      })
      .map(this._convertToLanguageOption);
  }

  getTwoLetterLanguageCode(language: Language): Language {
    switch (language) {
      case Language.ENGLISH:
      case Language.ENGLISH_GB:
      case Language.ENGLISH_PA:
      case Language.ENGLISH_US:
        return Language.ENGLISH;
      case Language.SWEDISH:
      case Language.SWEDISH_SE:
      case Language.SWEDISH_PA:
        return Language.SWEDISH;
      case Language.GERMAN:
      case Language.GERMAN_DE:
      case Language.GERMAN_CH:
        return Language.GERMAN;
      case Language.FRENCH:
      case Language.FRENCH_FR:
        return Language.FRENCH;
      case Language.SPANISH:
      case Language.SPANISH_ES:
        return Language.SPANISH;
      default:
        return Language.ENGLISH;
    }
  }

  loadTranslations(language: Language) {
    this._tolgee.tolgee.loadRecord({ language }).then(() => {
      this.translationsLoaded$.next([...new Set([...this.translationsLoaded$.value, language])]);
    });
  }

  private _convertToLanguageOption(language: Language): LanguageOption {
    return {
      code: Language[language],
      name: UI_STRINGS.LANGUAGE_NAMES[language],
    };
  }

  private _getUserLanguage() {
    const cookieLanguage = this._cookies.getUserLanguage();

    if (cookieLanguage) {
      this.language = this.getFourLetterLanguageCode(cookieLanguage);
      return;
    }

    let userLang: string = navigator.language || (navigator as any).userLanguage;

    if (this._supportedLanguages.some((l) => userLang.includes(l))) {
      if (this._supportedLanguageVariants.includes(<Language>userLang)) {
        this.language = <Language>userLang;
      } else {
        this.language = this.getFourLetterLanguageCode(<Language>userLang.split('-')[0]);
      }
    } else {
      this.language = DEFAULT_LANGUAGE;
    }
  }
}
